import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/client/components/ui/card";
import { Skeleton } from "@/client/components/ui/skeleton";
import {
  type AdminCapacityRequest,
  type InquiryScope,
  capacityRequestsAdminApi,
} from "@/client/features/admin/capacity-requests-admin-api";
import { ApiError } from "@/client/lib/api";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function StaffInquiriesPage() {
  const [scope, setScope] = useState<InquiryScope>("submitted");
  const [requests, setRequests] = useState<AdminCapacityRequest[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRequests(null);
    setError(false);
    capacityRequestsAdminApi
      .list(scope)
      .then((res) => {
        if (!cancelled) {
          setRequests(res.requests);
          setTruncated(res.truncated);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load inquiries.");
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Inquiries"
        description="Capacity requests submitted by prospects through the public intake form."
      >
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={scope === "submitted" ? "default" : "outline"}
            onClick={() => setScope("submitted")}
          >
            Submitted
          </Button>
          <Button
            size="sm"
            variant={scope === "all" ? "default" : "outline"}
            onClick={() => setScope("all")}
          >
            Include drafts
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{scope === "all" ? "All inquiries" : "Submitted inquiries"}</CardTitle>
        </CardHeader>
        <CardContent>
          {error && requests === null ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load inquiries. Refresh to try again.
            </p>
          ) : requests === null ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : requests.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {scope === "all" ? "No inquiries yet." : "No submitted inquiries yet."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="py-2 pr-4 font-medium">Company</th>
                    <th className="py-2 pr-4 font-medium">Contact</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">
                        <Link to={`/admin/inquiries/${r.id}`} className="hover:underline">
                          {r.company ?? <span className="text-muted-foreground">Unnamed</span>}
                        </Link>
                      </td>
                      <td className="py-2 pr-4">
                        {r.contactName ?? <span className="text-muted-foreground">—</span>}
                        {r.role ? <span className="text-muted-foreground"> · {r.role}</span> : null}
                      </td>
                      <td className="text-muted-foreground py-2 pr-4">{r.email}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={r.status === "submitted" ? "secondary" : "outline"}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground py-2 pr-4">
                        {formatDate(r.submittedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {truncated ? (
                <p className="text-muted-foreground mt-3 text-xs">
                  Showing the most recent {requests.length} inquiries. Older records aren&apos;t
                  displayed.
                </p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
